import { createLeaveRequestSchema, type LeaveStatus } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../api/http';
import { Notice, errorMessage, primaryButton, secondaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { ChildHomework } from '../homework/ChildHomework';
import { ChildDocuments } from './ChildDocuments';
import { ChildFees } from './ChildFees';
import { ChildHostel } from './ChildHostel';
import { ChildLibrary } from './ChildLibrary';
import { ChildResults } from './ChildResults';
import { ChildTransport } from './ChildTransport';
import { TimetableGrid, type Lesson, type Period } from '../timetable/TimetableGrid';

const TABS = [
  'attendance',
  'timetable',
  'homework',
  'results',
  'fees',
  'library',
  'transport',
  'hostel',
  'documents',
  'leave',
] as const;
type Tab = (typeof TABS)[number];

const isoDay = (offsetDays = 0) =>
  new Date(Date.now() + offsetDays * 86_400_000).toLocaleDateString('en-CA');

/** A parent's view of one child: attendance, class timetable and leave requests. */
export function ChildPage() {
  const { studentId = '' } = useParams();
  const [tab, setTab] = useState<Tab>('attendance');
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link to="/my-children" className="text-sm text-brand-700 underline">
        {t('child.back')}
      </Link>
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((name) => (
          <button
            key={name}
            role="tab"
            type="button"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === name ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-600'}`}
          >
            {t(`child.tab.${name}` as MessageKey)}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === 'attendance' && <ChildAttendance studentId={studentId} />}
        {tab === 'timetable' && <ChildTimetable studentId={studentId} />}
        {tab === 'homework' && <ChildHomework studentId={studentId} />}
        {tab === 'results' && <ChildResults studentId={studentId} />}
        {tab === 'fees' && <ChildFees studentId={studentId} />}
        {tab === 'library' && <ChildLibrary studentId={studentId} />}
        {tab === 'transport' && <ChildTransport studentId={studentId} />}
        {tab === 'hostel' && <ChildHostel studentId={studentId} />}
        {tab === 'documents' && <ChildDocuments studentId={studentId} />}
        {tab === 'leave' && <ChildLeave studentId={studentId} />}
      </div>
    </div>
  );
}

interface History {
  records: { date: string; status: string; remarks: string | null }[];
  summary: {
    present: number;
    absent: number;
    late: number;
    excused: number;
    halfDay: number;
    percentage: number | null;
  };
}

function ChildAttendance({ studentId }: { studentId: string }) {
  const from = isoDay(-29);
  const to = isoDay();
  const history = useQuery({
    queryKey: ['me', 'children', studentId, 'attendance', from, to],
    queryFn: () => api<History>(`/me/children/${studentId}/attendance`, { query: { from, to } }),
  });
  if (history.isPending) return <p role="status">{t('common.loading')}</p>;
  if (history.isError) return <Notice kind="error">{errorMessage(history.error)}</Notice>;
  const { summary, records } = history.data;
  return (
    <section aria-label={t('att.history')} className="space-y-3">
      <p className="text-sm text-slate-600">{t('att.range')}</p>
      <p className="text-3xl font-bold">
        {summary.percentage === null ? '—' : `${summary.percentage}%`}
        <span className="ml-2 text-sm font-normal text-slate-600">{t('att.percentage')}</span>
      </p>
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
        {(['present', 'absent', 'late', 'excused', 'half_day'] as const).map((s) => (
          <div key={s} className="rounded border border-slate-200 bg-white p-2">
            <dt className="text-slate-600">{t(`att.status.${s}` as MessageKey)}</dt>
            <dd className="text-lg font-semibold">{summary[s === 'half_day' ? 'halfDay' : s]}</dd>
          </div>
        ))}
      </dl>
      {records.length === 0 ? (
        <p className="text-slate-600">{t('att.noRecords')}</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-600">
            <tr>
              <th scope="col" className="p-2">
                {t('att.date.col')}
              </th>
              <th scope="col" className="p-2">
                {t('att.status')}
              </th>
              <th scope="col" className="p-2">
                {t('att.remarks')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {records.map((r) => (
              <tr key={r.date}>
                <td className="p-2">{r.date}</td>
                <td className="p-2">{t(`att.status.${r.status}` as MessageKey)}</td>
                <td className="p-2">{r.remarks ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ChildTimetable({ studentId }: { studentId: string }) {
  const timetable = useQuery({
    queryKey: ['me', 'children', studentId, 'timetable'],
    queryFn: () =>
      api<{ periods: Period[]; entries: Lesson[] }>(`/me/children/${studentId}/timetable`),
  });
  if (timetable.isPending) return <p role="status">{t('common.loading')}</p>;
  if (timetable.isError) return <Notice kind="error">{errorMessage(timetable.error)}</Notice>;
  return (
    <div className="space-y-3">
      {timetable.data.entries.length === 0 && <p className="text-slate-600">{t('tt.empty')}</p>}
      <TimetableGrid
        periods={timetable.data.periods}
        lessons={timetable.data.entries}
        show="teacher"
      />
    </div>
  );
}

interface Leave {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string;
  status: LeaveStatus;
  reviewNote: string | null;
}

function ChildLeave({ studentId }: { studentId: string }) {
  const queryClient = useQueryClient();
  const key = ['me', 'children', studentId, 'leave'];
  const [form, setForm] = useState({ fromDate: '', toDate: '', reason: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const list = useQuery({
    queryKey: key,
    queryFn: () => api<Leave[]>(`/me/children/${studentId}/leave-requests`),
  });
  const create = useMutation({
    mutationFn: (body: object) =>
      api(`/me/children/${studentId}/leave-requests`, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ fromDate: '', toDate: '', reason: '' });
      setMessage({ kind: 'ok', text: t('leave.sent') });
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      api(`/me/children/${studentId}/leave-requests/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  return (
    <section aria-label={t('leave.mine')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <form
        noValidate
        aria-label={t('leave.request')}
        onSubmit={(e) => {
          e.preventDefault();
          const parsed = createLeaveRequestSchema.safeParse(form);
          if (!parsed.success)
            return setErrors(
              Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
            );
          setErrors({});
          setMessage(null);
          create.mutate(parsed.data);
        }}
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
      >
        <TextField
          label={t('leave.from')}
          type="date"
          value={form.fromDate}
          error={errors.fromDate}
          onChange={(e) => setForm({ ...form, fromDate: e.target.value })}
        />
        <TextField
          label={t('leave.to')}
          type="date"
          value={form.toDate}
          error={errors.toDate}
          onChange={(e) => setForm({ ...form, toDate: e.target.value })}
        />
        <TextField
          className="sm:col-span-2"
          label={t('leave.reason')}
          value={form.reason}
          error={errors.reason}
          maxLength={500}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
        <div>
          <button type="submit" className={primaryButton} disabled={create.isPending}>
            {t('leave.send')}
          </button>
        </div>
      </form>
      {list.data?.length === 0 && <p className="text-slate-600">{t('leave.empty')}</p>}
      <ul className="space-y-2">
        {list.data?.map((r) => (
          <li key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`} — {r.reason}
              </span>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                {t(`leave.status.${r.status}` as MessageKey)}
              </span>
            </div>
            {r.reviewNote && <p className="mt-1 text-slate-600">{r.reviewNote}</p>}
            {r.status === 'pending' && (
              <button
                type="button"
                className={`${secondaryButton} mt-2`}
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(r.id)}
              >
                {t('leave.cancel')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
